import { css } from '@emotion/react'
import { TransitionLoader } from '@opallabs/kuiper'
import type { WithTheme } from '@opallabs/lapidary'
import { NotificationItem, NotificationText } from '@opallabs/satellite'
import { Notification } from '@opallabs/wormhole/types'
import { EmptyState } from './EmptyState'
import * as styles from './styles'
import { NotificationPanelProps } from './types'
import { useNotificationsPanelData } from './useNotificationsPanelData'

export type NotificationWrapperProps = Readonly<
  Notification &
    WithTheme & {
      onItemClick?: () => void
      onRemove?: (id: string) => void
    }
>

const NotificationWrapper: React.FC<NotificationWrapperProps> = ({
  downloadUrl,
  heading,
  id,
  onItemClick,
  onRemove,
  placeholder,
  read,
  referencedObjects,
  seen,
  subHeading,
  supplementalText,
  theme = 'light',
  timeFormat,
  timestamp,
  type,
}) => {
  const showHeading = heading !== undefined
  const showSubHeading = subHeading !== undefined

  return (
    <li>
      {showHeading && (
        <header css={css(styles.header({ theme }))}>
          <div css={css(styles.headerTitle)}>{heading}</div>
          {showSubHeading && (
            <div css={css(styles.headerSubtitle({ theme }))}>{subHeading}</div>
          )}
        </header>
      )}
      <NotificationItem
        downloadUrl={downloadUrl}
        filename={
          referencedObjects && referencedObjects.length > 0
            ? referencedObjects[0].text
            : ''
        }
        id={id}
        onItemClick={onItemClick}
        onRemove={onRemove}
        placeholder={placeholder}
        read={read}
        seen={seen}
        theme={theme}
        timeFormat={timeFormat}
        timestamp={timestamp}
      >
        <NotificationText
          referencedObjects={referencedObjects}
          supplementalText={supplementalText}
          theme={theme}
          type={type}
        />
      </NotificationItem>
    </li>
  )
}
NotificationWrapper.displayName =
  'Darwin: NotificationWrapper ← NotificationsPanel ← PersonalPanel'

export const NotificationsPanel: React.FC<NotificationPanelProps> = ({
  createCheckpoint,
  getNotifications,
  isLoading,
  notifications,
  onItemClick,
  onRemoveNotification,
  theme = 'light',
  type,
}) => {
  const { entities, isEmpty } = useNotificationsPanelData({
    createCheckpoint,
    getNotifications,
    isLoading,
    notifications,
    onItemClick,
    onRemoveNotification,
  })

  if (!isLoading && isEmpty) {
    return <EmptyState theme={theme} type={type} />
  }

  return (
    <TransitionLoader
      loaded={!isLoading}
      minHeight={132} // this is an arbitrary size that looks good
      theme="transparent"
      styleOverride={{ overflowY: 'scroll' }}
    >
      <ul css={css(styles.list({ theme }))}>
        {entities.map((entity, i) => (
          <NotificationWrapper
            key={`notification-${i}`}
            theme={theme}
            {...entity}
          />
        ))}
      </ul>
    </TransitionLoader>
  )
}
NotificationsPanel.displayName = 'Darwin: NotificationsPanel ← PersonalPanel'
